import React, { forwardRef, useImperativeHandle, useState, useRef, useEffect } from 'react';
import { FiLogIn, FiLogOut, FiRotateCcw, FiRotateCw, FiChevronsUp, FiChevronsDown, FiGitMerge, FiCheckSquare, FiRefreshCcw, FiFileText } from 'react-icons/fi';
import { useEditing } from '../EditingContext';
import { usePlanName } from '../PlanNameContext';
import { useHasChanges } from '../HasChangesContext';
import { useConfirmAlert } from '../hooks/useConfirmAlert';
import config from '../config';

const Header = forwardRef(({ 
  header, 
  user, 
  onLoginOpen, 
  onLogout, 
  undoRedo, 
  onToggleAllPanels, 
  areAllPanelsClosed,
  autosaveFunction,
  silentAutosaveFunction,
  onMergeDuplicateGroups,
  onBatchOperations,
  selectedTasksCount = 0,
  batchOperations,
  hasDuplicates = false,
  onGlobalSync,
  currentLoadedPlanId,
  planMetadata,
  onPdfExportOpen
}, ref) => {
  const { showEditButtons, setShowEditButtons, canEdit, isLearner } = useEditing();
  const { currentPlanName } = usePlanName();
  const { hasChanges } = useHasChanges();
  const { alert: modalAlert } = useConfirmAlert();
  
  const {
    canUndo,
    canRedo,
    handleUndo,
    handleRedo,
    undoHistory,
    currentHistoryIndex
  } = undoRedo;

  // Autosave State
  const [autosaveEnabled, setAutosaveEnabled] = useState(true);
  const [lastAutosave, setLastAutosave] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const autosaveIntervalRef = useRef(null);
  const hasChangesRef = useRef(false);
  const isSavingRef = useRef(false);
  const saveConfirmationTimeoutRef = useRef(null);

  // CSS für Spinner hinzufügen (einmalig)
  useEffect(() => {
    if (typeof document !== 'undefined' && !document.getElementById('autosave-spinner-styles')) {
      const style = document.createElement('style');
      style.id = 'autosave-spinner-styles';
      style.textContent = `
        @keyframes autosave-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes fadeIn {
          0% { 
            opacity: 0; 
            transform: translateY(-5px); 
          }
          100% { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }

        @keyframes pulse {
          0% {
            opacity: 1;
          }
          50% {
            opacity: 0.6;
          }
          100% {
            opacity: 1;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Aktualisiere hasChanges und isSaving Refs
  useEffect(() => {
    hasChangesRef.current = hasChanges;
  }, [hasChanges]);

  useEffect(() => {
    isSavingRef.current = isSaving;
  }, [isSaving]);

  // Bestimme ob Autosave-Schalter überhaupt angezeigt werden soll
  const shouldShowAutosave = React.useCallback(() => {
    // Autosave nur anzeigen wenn:
    // 1. Plan aus der Datenbank geladen wurde (currentLoadedPlanId vorhanden)
    // 2. Plan bereits in dieser Session gespeichert wurde (localStorage)
    if (currentLoadedPlanId) {
      return true;
    }
    
    if (user?.username && currentPlanName) {
      const planSaveKey = `plan_saved_${currentPlanName}_${user.username}`;
      return localStorage.getItem(planSaveKey) === 'true';
    }
    
    return false;
  }, [currentLoadedPlanId, user?.username, currentPlanName]);





  // Handler für Klick auf Planname
  const handlePlanNameClick = async () => {
    await modalAlert("Nutzen Sie 'Speichern als', um den Plan umzubenennen", {
      title: "Plan umbenennen",
      confirmText: "OK"
    });
  };

  // Autosave standardmäßig aktivieren beim ersten Laden, aber Benutzereinstellung respektieren
  useEffect(() => {
    if (!hasInitialized) {
      // Prüfe ob der Benutzer eine gespeicherte Präferenz hat
      const userPreference = localStorage.getItem('userAutosavePreference');
      
      if (userPreference !== null) {
        // Benutzer hat eine Auswahl getroffen - respektiere diese
        setAutosaveEnabled(userPreference === 'true');
      } else {
        // Keine Benutzerauswahl vorhanden - standardmäßig einschalten
        setAutosaveEnabled(true);
      }
      setHasInitialized(true);
    }
  }, [hasInitialized]);

  // Autosave beim Einschalten des Edit-Modus aktivieren (aber Benutzerauswahl respektieren)
  useEffect(() => {
    if (hasInitialized && showEditButtons && shouldShowAutosave()) {
      // Prüfe ob der Benutzer eine gespeicherte Präferenz hat
      const userPreference = localStorage.getItem('userAutosavePreference');
      
      // Nur automatisch aktivieren wenn der Benutzer keine explizite Auswahl getroffen hat
      if (userPreference === null) {
        const isNewUnnamedPlan = currentPlanName === 'unbenannt' && !currentLoadedPlanId;
        if (!isNewUnnamedPlan) {
          // Für existierende Pläne: Autosave standardmäßig aktivieren (nur wenn keine Benutzerauswahl)
          setAutosaveEnabled(true);
        }
      }
      // Wenn userPreference vorhanden ist, wird die Benutzerauswahl respektiert (keine Änderung)
    }
  }, [showEditButtons, hasInitialized, currentLoadedPlanId, currentPlanName, shouldShowAutosave]);

  // Überwache localStorage Änderungen um Autosave-Sichtbarkeit dynamisch zu aktualisieren
  // ABER erzwinge NICHT das Einschalten wenn der User es ausgeschaltet hat
  useEffect(() => {
    const handleStorageChange = () => {
      // Nur re-evaluation der Sichtbarkeit, aber keine automatische Aktivierung
      // Das shouldShowAutosave wird automatisch durch das Re-Rendering aktualisiert
    };

    // Listen to storage events (from other tabs/windows)
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [currentPlanName, user?.username]);

  // Stille Autosave-Funktion
  const handleSilentAutosave = React.useCallback(async () => {
    if (!silentAutosaveFunction || typeof silentAutosaveFunction !== 'function') {
      return;
    }

    setIsSaving(true);
    try {
      const success = await silentAutosaveFunction();
      if (success) {
        setLastAutosave(new Date());
      }
    } catch (error) {
      console.error('Autosave Fehler:', error);
    } finally {
      setIsSaving(false);
    }
  }, [silentAutosaveFunction]);

  // Autosave-Intervall verwalten
  useEffect(() => {
    // Entferne vorhandenes Intervall
    if (autosaveIntervalRef.current) {
      clearInterval(autosaveIntervalRef.current);
      autosaveIntervalRef.current = null;
    }

    // Erstelle neues Intervall wenn autosave aktiviert ist und eine Funktion vorhanden ist
    if (autosaveEnabled && silentAutosaveFunction && typeof silentAutosaveFunction === 'function') {
      autosaveIntervalRef.current = setInterval(() => {
        const currentHasChanges = hasChangesRef.current;
        const currentIsSaving = isSavingRef.current;
        
        if (currentHasChanges && !currentIsSaving) {
          handleSilentAutosave();
        }
      }, config.AUTOSAVE.INTERVAL_MS); // Intervall aus Config (10 Sekunden)
    }

    // Cleanup beim Unmount oder wenn sich die Abhängigkeiten ändern
    return () => {
      if (autosaveIntervalRef.current) {
        clearInterval(autosaveIntervalRef.current);
        autosaveIntervalRef.current = null;
      }
    };
  }, [autosaveEnabled, handleSilentAutosave, silentAutosaveFunction]);

  // Cleanup beim Unmount
  useEffect(() => {
    return () => {
      if (saveConfirmationTimeoutRef.current) {
        clearTimeout(saveConfirmationTimeoutRef.current);
      }
    };
  }, []);

  const handleAutosaveToggle = () => {
    const newValue = !autosaveEnabled;
    setAutosaveEnabled(newValue);
    
    // Speichere die Benutzerauswahl im localStorage
    localStorage.setItem('userAutosavePreference', newValue.toString());
  };

  // Funktion für Ctrl+S Speicher-Bestätigung
  const showCtrlSaveConfirmation = React.useCallback(() => {
    // Entferne vorhandenes Timeout
    if (saveConfirmationTimeoutRef.current) {
      clearTimeout(saveConfirmationTimeoutRef.current);
    }
    
    setShowSaveConfirmation(true);
    
    // Nach 2 Sekunden ausblenden
    saveConfirmationTimeoutRef.current = setTimeout(() => {
      setShowSaveConfirmation(false);
      saveConfirmationTimeoutRef.current = null;
    }, 2000);
  }, []);

  // Expose Funktionen nach außen
  useImperativeHandle(ref, () => ({
    showCtrlSaveConfirmation
  }));

  return (
    <div style={{
      height: 80,
      background: '#222',
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      paddingLeft: 24,
      fontSize: 24,
      flexShrink: 0,
      justifyContent: 'space-between'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <img 
          src="/logo.png" 
          alt="Logo" 
          title={`Version ${process.env.REACT_APP_BUILD_DATE || new Date().toLocaleDateString('de-DE')}`}
          style={{ 
            height: 50, 
            width: 'auto',
            objectFit: 'contain'
          }} 
        />
        <span> {header || 'Header'}</span>
        
        {/* Planname und Status-Indikatoren - nur für Nicht-Lernende */}
        {!isLearner && (
          <>
            <div style={{ 
              color: '#666', 
              fontSize: 16, 
              margin: '0 8px',
              userSelect: 'none'
            }}>
              |
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span 
                style={{ 
                  fontSize: 18,
                  color: '#ccc',
                  padding: '4px 8px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s'
                }}
                title="Nutzen Sie 'Speichern als', um den Plan umzubenennen"
                onClick={handlePlanNameClick}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                {currentPlanName || 'unbenannt'}
              </span>
              
              {/* Übersichtsplan-Badge */}
              {planMetadata?.isOverviewPlan && (
                <div 
                  style={{
                    backgroundColor: '#4caf50',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    padding: '3px 8px',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)',
                    cursor: 'help'
                  }}
                  title={planMetadata?.sourcePlans?.length > 0 
                    ? `Zusammengeführte Pläne:\n${planMetadata.sourcePlans.map(p => `• ${p.name} von ${p.user_id} (${p.taskCount} Tasks)`).join('\n')}`
                    : 'Virtueller Übersichtsplan - Zusammenführung aller zugeordneten Pläne'
                  }
                >
                  <span>🔗</span>
                  ÜBERSICHT
                  {planMetadata?.sourcePlans?.length > 0 && (
                    <span style={{
                      backgroundColor: 'rgba(255,255,255,0.3)',
                      borderRadius: '8px',
                      padding: '1px 4px',
                      fontSize: '9px',
                      marginLeft: '2px'
                    }}>
                      {planMetadata.sourcePlans.length}
                    </span>
                  )}
                </div>
              )}
              
              {/* Lernenden-Plan-Badge */}
              {planMetadata?.isStudentPlan && !planMetadata?.isOverviewPlan && (
                <div style={{
                  backgroundColor: '#2196f3',
                  color: 'white',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  padding: '3px 8px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  boxShadow: '0 2px 4px rgba(33, 150, 243, 0.3)'
                }}>
                  <span>👤</span>
                  LERNENDEN-PLAN
                </div>
              )}
            </div>

            {/* Änderungs-Indikator */}
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: hasChanges ? '#ff9800' : '#4caf50',
                marginLeft: 4,
                animation: hasChanges ? 'pulse 2s infinite' : 'none',
                transition: 'background-color 0.3s ease'
              }}
              title={hasChanges ? "Ungespeicherte Änderungen (CTRL-S zum sofort speichern)" : "Alle Änderungen gespeichert"}
            />
          </>
        )}
        
        {/* Ctrl+S Speicher-Bestätigung - nur für Nicht-Lernende */}
        {showSaveConfirmation && !isLearner && (
          <div
            style={{
              marginLeft: 8,
              padding: '4px 8px',
              backgroundColor: '#4caf50',
              color: 'white',
              borderRadius: 4,
              fontSize: 12,
              fontWeight: 'bold',
              animation: 'fadeIn 0.3s ease-in-out',
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}
          >
            ✓ Gespeichert {lastAutosave && `um ${lastAutosave.toLocaleTimeString()}`}
          </div>
        )}
        
        {/* Autosave Switch - nur anzeigen für gespeicherte/geladene Pläne UND im Edit-Modus */}
        {shouldShowAutosave() && showEditButtons && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 6, 
            marginLeft: 12 
          }}>
            <span style={{ 
              fontSize: 12, 
              color: '#aaa', 
              whiteSpace: 'nowrap',
              userSelect: 'none'
            }}>
              Autosave
            </span>
            
            {/* Saving Spinner */}
            {isSaving && (
              <div
                className="autosave-spinner"
                style={{
                  width: 12,
                  height: 12,
                  border: '2px solid #666',
                  borderTop: '2px solid #4caf50',
                  borderRadius: '50%',
                  marginLeft: 2,
                  animation: 'autosave-spin 1s linear infinite'
                }}
                title="Speichere..."
              />
            )}
            <label
              style={{
                position: 'relative',
                display: 'inline-block',
                width: 36,
                height: 20,
                cursor: 'pointer'
              }}
              title={autosaveEnabled 
                ? `Autosave aktiviert (alle ${config.AUTOSAVE.INTERVAL_MS / 1000}s)${lastAutosave ? ` - Letztes Autosave: ${lastAutosave.toLocaleTimeString()}` : ''}` 
                : "Autosave deaktiviert - klicken zum aktivieren"
              }
            >
              <input
                type="checkbox"
                checked={autosaveEnabled}
                onChange={handleAutosaveToggle}
                style={{
                  opacity: 0,
                  width: 0,
                  height: 0
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: autosaveEnabled ? '#4caf50' : '#666',
                  borderRadius: 20,
                  transition: 'all 0.3s ease'
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    content: '',
                    height: 16,
                    width: 16,
                    left: autosaveEnabled ? 18 : 2,
                    top: 2,
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    transition: 'left 0.3s ease',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                  }}
                />
              </span>
            </label>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, paddingRight: 24 }}>
        
        {/* Batch Operations Button - Zweistufig mit separaten Klickbereichen */}
        {showEditButtons && batchOperations && (
          <div
            style={{
              background: !batchOperations.isSelectionMode 
                ? '#6c757d' 
                : selectedTasksCount > 0 
                  ? '#17a2b8' 
                  : '#28a745',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              padding: '8px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 14,
              fontWeight: 500,
              boxShadow: selectedTasksCount > 0 
                ? '0 2px 4px rgba(23, 162, 184, 0.3)' 
                : batchOperations.isSelectionMode 
                  ? '0 2px 4px rgba(40, 167, 69, 0.3)' 
                  : 'none'
            }}
          >
            {/* Checkbox-Bereich - immer klickbar für Toggle */}
            <div 
              onClick={(e) => {
                e.stopPropagation();
                batchOperations.toggleSelectionMode();
              }}
              style={{
                width: '16px',
                height: '16px',
                border: '2px solid #fff',
                borderRadius: '3px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: batchOperations.isSelectionMode ? '#fff' : 'transparent',
                marginRight: '2px',
                cursor: 'pointer'
              }}
              title={batchOperations.isSelectionMode ? 'Auswahl-Modus beenden' : 'Auswahl-Modus aktivieren'}
            >
              {batchOperations.isSelectionMode && (
                <FiCheckSquare style={{ color: '#28a745', fontSize: '12px' }} />
              )}
            </div>
            
            {/* Text-Bereich - klickbar für Batch-Modal */}
            <div
              onClick={() => {
                if (!batchOperations.isSelectionMode) {
                  // Wenn Auswahl-Modus nicht aktiv, erst aktivieren
                  batchOperations.toggleSelectionMode();
                } else if (selectedTasksCount > 0) {
                  // Wenn Tasks ausgewählt, Modal öffnen
                  onBatchOperations && onBatchOperations();
                }
              }}
              style={{
                cursor: (!batchOperations.isSelectionMode || selectedTasksCount > 0) ? 'pointer' : 'default',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              title={
                !batchOperations.isSelectionMode 
                  ? 'Tasks auswählen für Batch-Operationen' 
                  : selectedTasksCount > 0 
                    ? `Batch-Operationen für ${selectedTasksCount} ausgewählte Tasks öffnen` 
                    : 'Wählen Sie zuerst Tasks aus (Checkboxen in den Tasks)'
              }
            >
              {!batchOperations.isSelectionMode ? 'Tasks auswählen' : 'Batch-Operationen'}
              
              {/* Counter nur anzeigen wenn Tasks ausgewählt sind */}
              {batchOperations.isSelectionMode && selectedTasksCount > 0 && (
                <span style={{
                  background: '#fff',
                  color: '#17a2b8',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  marginLeft: '4px'
                }}>
                  {selectedTasksCount}
                </span>
              )}
            </div>
          </div>
        )}
        
        {/* Global Sync Button */}
        {showEditButtons && onGlobalSync && currentPlanName !== 'unbenannt' && (
          <button
            onClick={onGlobalSync}
            style={{
              background: '#17a2b8',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              padding: '8px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 14,
              fontWeight: 500,
              boxShadow: '0 2px 4px rgba(23, 162, 184, 0.3)'
            }}
            title="Alle Tasks basierend auf deren Klassennamen synchronisieren"
          >
            <FiRefreshCcw />
            Lernende synchronisieren
          </button>
        )}

        {/* Merge Duplicates Button - nur anzeigen wenn Duplikate vorhanden sind */}
        {showEditButtons && onMergeDuplicateGroups && hasDuplicates && (
          <button
            onClick={onMergeDuplicateGroups}
            style={{
              background: '#ff9800',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              padding: '8px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 14,
              fontWeight: 500,
              boxShadow: '0 2px 4px rgba(255, 152, 0, 0.3)'
            }}
            title="Panelgruppen und/oder Panels mit gleichen Namen zusammenführen"
          >
            <FiGitMerge />
            Duplikate zusammenführen
          </button>
        )}
        
        {/* Undo/Redo Buttons - kompakter */}
        {showEditButtons && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4
          }}>
            <div style={{
              display: 'flex',
              gap: 4
            }}>
              <button
                onClick={handleUndo}
                disabled={!canUndo}
                style={{
                  background: canUndo ? '#ff9800' : '#ccc',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 3,
                  padding: 6,
                  cursor: canUndo ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  opacity: canUndo ? 1 : 0.6
                }}
                title={`Rückgängig${canUndo ? ` (${currentHistoryIndex} von ${undoHistory.length - 1})` : ' (nicht verfügbar)'}`}
              >
                <FiRotateCcw size={16} />
              </button>
              <button
                onClick={handleRedo}
                disabled={!canRedo}
                style={{
                  background: canRedo ? '#ff9800' : '#ccc',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 3,
                  padding: 6,
                  cursor: canRedo ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  opacity: canRedo ? 1 : 0.6
                }}
                title={`Wiederholen${canRedo ? ` (${undoHistory.length - currentHistoryIndex - 1} Schritte voraus)` : ' (nicht verfügbar)'}`}
              >
                <FiRotateCw size={16} />
              </button>
            </div>
            {/* Undo-Historie-Anzeige - kompakter unter den Buttons */}
            {undoHistory.length > 0 && (
              <div style={{
                fontSize: '10px',
                color: '#888',
                padding: '2px 4px',
                background: '#333',
                borderRadius: '2px',
                display: 'flex',
                alignItems: 'center',
                whiteSpace: 'nowrap'
              }}>
                {currentHistoryIndex + 1}/{undoHistory.length}
              </div>
            )}
          </div>
        )}
        
        {/* Edit-Mode Toggle Button - nur wenn Benutzer angemeldet ist und Edit-Berechtigung hat */}
        {canEdit && (
          <button
            onClick={() => setShowEditButtons(!showEditButtons)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              padding: '4px',
              transition: 'all 0.3s ease'
            }}
            title={showEditButtons ? 'Edit-Funktion ausschalten' : 'Edit-Funktion einschalten'}
          >
            <div
              style={{
                width: '44px',
                height: '24px',
                backgroundColor: showEditButtons ? '#4caf50' : '#666',
                borderRadius: '12px',
                position: 'relative',
                transition: 'all 0.3s ease'
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '2px',
                  left: showEditButtons ? '22px' : '2px',
                  width: '20px',
                  height: '20px',
                  backgroundColor: '#fff',
                  borderRadius: '50%',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              />
            </div>
          </button>
        )}
        
        {/* PDF Export Button */}
        <button
          onClick={onPdfExportOpen}
          style={{
            background: '#17a2b8',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 500,
            boxShadow: '0 2px 4px rgba(23, 162, 184, 0.3)'
          }}
          title="Plan als PDF exportieren"
        >
          <FiFileText />
          PDF
        </button>

        {/* Toggle All Panels Button */}
        <button
          onClick={onToggleAllPanels}
          style={{
            background: '#666',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: 18,
            marginRight: '8px',
            width: '36px',
            height: '36px'
          }}
          title={areAllPanelsClosed() ? "Alle Panels öffnen" : "Alle Panels schließen"}
        >
          {areAllPanelsClosed() ? <FiChevronsDown size={20} /> : <FiChevronsUp size={20} />}
        </button>

        {/* Login/Logout */}
        {!user ? (
          <button
            onClick={onLoginOpen}
            style={{
              background: '#444',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              padding: '8px 16px',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              fontSize: 18
            }}
          >
            <FiLogIn style={{ marginRight: 8 }} />
            Login
          </button>
        ) : (
          <>
            <span style={{ fontSize: 18 }}>{user.name}</span>
            <button
              onClick={onLogout}
              style={{
                background: '#444',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                padding: '8px 16px',
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                fontSize: 18
              }}
            >
              <FiLogOut style={{ marginRight: 8 }} />
              Logout
            </button>
          </>
        )}
      </div>
    </div>
  );
});

Header.displayName = 'Header';

export default Header;
